import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { Inbox } from "lucide-react";
import Avatar from "../../../../../../../../../components/shared/Avatar";
import ShiftTooltip from "../../../../../../components/tooltip/ShiftTooltip";
import SectionInfo from "../../../../../../components/SectionInfo";
import { useMenuContext } from "../../../../../../hooks/context/useMenuContext";
import { useScheduleContext } from "../../../../../../hooks/context/useScheduleContext";
import { usePreferences } from "../../../../../../../../Settings/Configuration/hooks/usePreferences";
import { useCompanyTimeFormat } from "../../../../../../../../../utils/hooks/company";
import { renderUserName } from "../../../../../../../../../utils/helpers/user";
import { mapOpenShiftRequest } from "../../../../../../helpers/data/mapOpenShiftRequest";
import { isOpenShiftRequest } from "../../../../../../helpers/data/isOpenShiftRequest";
import { palette } from "../../../../../../../../Settings/Configuration/Schedule/Team/Settings/Shift/colors";
import { SCHEDULE_PREFERENCE_KEYS } from "../../../../../../constants/scheduleTypes";

function OpenShifts({ hiddenEvents }: any) {
    const { t } = useTranslation();
    const timeFormat = useCompanyTimeFormat();
    const userTimezone = moment.tz.guess();
    const { openMenu } = useMenuContext();
    const { type } = useScheduleContext();
    const preferences = usePreferences(SCHEDULE_PREFERENCE_KEYS[type]);

    const openShiftStyle = useMemo(() => {
        const colors = palette?.openShift?.[preferences?.colors?.openShift ?? 0];
        return { backgroundColor: colors?.bg, borderColor: colors?.border };
    }, [preferences]);

    const data = useMemo(() => {
        return hiddenEvents.filter((event: any) => isOpenShiftRequest(event.type));
    }, [hiddenEvents]);

    const onContextMenu = useCallback((e: any, shift: any) => {
        e.preventDefault();
        e.stopPropagation();
        openMenu({
            id: "open-shift-menu",
            x: e.clientX,
            y: e.clientY + 10,
            selected: shift,
            moreModal: true
        })
    }, [openMenu]);

    return data?.length === 0 ? (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-neutral-600 tw-py-7 tw-border tw-border-dashed tw-rounded-sm">
            <Inbox />
            {t("no-data")}
        </div>
    ) : (
        <div>
            {data.map((event: any) => {
                const shift = mapOpenShiftRequest(event, userTimezone);
                return (
                    <ShiftTooltip data={{ ...shift, type: "open-shift" }}>
                        <div
                            className="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-border-2 tw-border-dashed tw-rounded-sm tw-px-3 tw-py-1 tw-border-neutral-300 tw-mb-1 tw-cursor-pointer"
                            style={openShiftStyle}
                            onContextMenu={(e: any) => onContextMenu(e, shift)}
                        >
                            <div className="tw-flex tw-gap-1 tw-items-center">
                                <Avatar user={shift.user} size="sm" />
                                <p className="tw-text-sm">{renderUserName(shift?.user)}</p>
                            </div>
                            <div className="tw-flex tw-items-center tw-text-sm tw-font-medium">
                                <span>
                                    {moment(shift?.startDate).format(timeFormat)}
                                </span>
                                <span className="tw-px-2">-</span>
                                <span>
                                    {moment(shift?.endDate).format(timeFormat)}
                                </span>
                            </div>
                            <SectionInfo
                                section={shift?.sections?.[0]}
                                className="tw-pt-0.5"
                            />
                        </div>
                    </ShiftTooltip>
                );
            })}
        </div>
    );
}

export default OpenShifts;
